import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectUserId } from '../../../../user/store/selectors/user.selectors';
import { AccessLogActor } from '../../models/access-log-actor';
import * as fromAccessLogs from '../reducer/access-logs.reducer';

export const selectAccessLogsFeature = createFeatureSelector<fromAccessLogs.AccessLogsState>(
  fromAccessLogs.accessLogsFeatureKey
);

const { selectAll } = fromAccessLogs.accessLogsAdapter.getSelectors();

export const selectAccessLogs = createSelector(selectAccessLogsFeature, selectAll);

export const selectError = createSelector(selectAccessLogsFeature, state => state.baseState.error);

export const selectIsLoading = createSelector(selectAccessLogsFeature, state => state.baseState.isLoading);

export const selectPageNumber = createSelector(selectAccessLogsFeature, state => state.baseState.pageNumber);

export const selectPageSize = createSelector(selectAccessLogsFeature, state => state.baseState.pageSize);

export const selectIsAtEndOfData = createSelector(selectAccessLogsFeature, state => state.baseState.isAtEndOfData);

export const selectTotalRecordCount = createSelector(
  selectAccessLogsFeature,
  state => state.baseState.totalRecordCount
);

export const selectFilters = createSelector(selectAccessLogsFeature, state => state.filters);

export const selectActors = createSelector(selectAccessLogsFeature, state => state.actors);

export const selectActorsError = createSelector(selectAccessLogsFeature, state => state.actorsError);

export const selectSelectedActor = createSelector(selectAccessLogsFeature, state => state.selectedActor);

export const selectHasLoadedActors = createSelector(selectAccessLogsFeature, state => state.hasLoadedActors);

/**
 * What the "Accessed by" pick list renders: the server's options for the current view, plus the current
 * selection when a Date/Type/search change has left it with no matching entries, so the open menu still
 * shows who the list is filtered to. It drops out once "All" or another person is picked.
 *
 * The signed-in account's own option — the one rendered "You" — leads, a kept selection included,
 * rather than trusting the server to lead with it. Anyone else is inserted by display name, which
 * approximates the server's order (its collation may differ from `localeCompare`).
 */
export const selectActorOptions = createSelector(
  selectActors,
  selectSelectedActor,
  selectUserId,
  (actors, selectedActor, currentUserId) => {
    const isOwn = (actor: AccessLogActor) => currentUserId != null && actor.patientPortalUserId === currentUserId;
    let options = actors;

    if (selectedActor && !actors.some(actor => actor.patientPortalUserId === selectedActor.patientPortalUserId)) {
      const insertAt = actors.findIndex(
        actor => !isOwn(actor) && actor.displayName.localeCompare(selectedActor.displayName) > 0
      );
      options =
        insertAt === -1
          ? [...actors, selectedActor]
          : [...actors.slice(0, insertAt), selectedActor, ...actors.slice(insertAt)];
    }

    const ownIndex = options.findIndex(isOwn);

    return ownIndex <= 0 ? options : [options[ownIndex], ...options.slice(0, ownIndex), ...options.slice(ownIndex + 1)];
  }
);
